import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Globe, Smartphone, Tablet, Wifi, HelpCircle } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { providers, usps as mobielUsps } from "@/data/mobiel";
import { BundelCard, BelBuitenEUCard } from "@/components/mobiel/BundelCards";
import logoOdido from "@/assets/logo-odido.webp";
import mobielHeroBg from "@/assets/mobiel-hero-bg.jpg";
import mobielHeroVideo from "@/assets/mobiel-hero-video.mp4";

const provider = providers.find((p) => p.slug === "odido")!;

const MobielOdidoPage = () => {
  const hasBuitenland = provider.buitenlandbundels && provider.buitenlandbundels.length > 0;

  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[40vh] flex items-center overflow-hidden">
        <video autoPlay muted loop playsInline poster={mobielHeroBg} className="absolute inset-0 h-full w-full object-cover">
          <source src={mobielHeroVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${provider.kleur}cc, ${provider.kleur}88 50%, rgba(0,0,0,0.4))` }} />
        <div className="absolute right-[-4%] top-1/2 -translate-y-1/2 pointer-events-none select-none opacity-[0.08]">
          <img loading="eager" decoding="async" fetchPriority="high" src={logoOdido} alt="" className="w-[340px] md:w-[480px] h-auto rotate-[-12deg] brightness-0 invert" />
        </div>
        <div className="container relative z-10 py-16">
          <Link to="/diensten/mobiel" className="inline-flex items-center text-white/80 hover:text-white mb-6 text-sm transition-colors">
            <ArrowLeft className="mr-2 h-4 w-4" /> Terug naar overzicht
          </Link>
          <motion.div
            className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white p-2.5 shadow-lg"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <img loading="lazy" decoding="async" src={logoOdido} alt="Odido logo" className="w-full h-full object-contain" />
          </motion.div>
          <motion.h1 className="text-3xl md:text-5xl font-bold text-white" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {provider.naam} Zakelijk Mobiel
          </motion.h1>
          <motion.p className="text-white/80 mt-2 max-w-lg" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            {provider.beschrijving}
          </motion.p>
          <motion.div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            {mobielUsps.map((usp, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${i === 0 ? "bg-primary" : i === 1 ? "bg-rinqel-blue" : i === 2 ? "bg-rinqel-green" : "bg-primary"}`} />
                <span className="text-white/90 text-sm font-medium">{usp.titel}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Abonnementen */}
      <section className="py-16 md:py-24">
        <div className="container">
          {/* Sim Only Mobiel */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl" style={{ backgroundColor: `${provider.kleur}15` }}>
                <Smartphone className="h-5 w-5" style={{ color: provider.kleur }} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">Sim Only — Mobiel</h2>
            </div>
            <p className="text-muted-foreground mb-8 ml-[52px]">Bellen, sms'en en internetten op je smartphone · Maandelijks opzegbaar</p>

            {(() => {
              const basisBundels = provider.abonnementen.slice(0, 4);
              const unlimitedBundels = provider.abonnementen.slice(4);
              const hasUnlimited = unlimitedBundels.length > 0;
              return (
                <>
                  <div className={`grid gap-4 ${hasUnlimited ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2 lg:grid-cols-3'} mb-6`}>
                    {(hasUnlimited ? basisBundels : provider.abonnementen).map((abo, i) => (
                      <motion.div key={abo.naam} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                        <Card className="h-full relative overflow-hidden transition-shadow hover:shadow-lg" style={{ borderColor: provider.kleur, boxShadow: `0 0 0 2px ${provider.kleur}` }}>
                          <div className="absolute top-0 left-0 right-0 text-center text-xs font-semibold text-white py-1 flex items-center justify-center gap-1.5" style={{ backgroundColor: provider.kleur }}>
                            <Smartphone className="h-3 w-3" />
                            {abo.naam}
                          </div>
                          <CardContent className="p-5 flex flex-col items-center text-center pt-9 h-full">
                            <div className="text-2xl font-bold mb-1" style={{ color: provider.kleur }}>{abo.prijs}</div>
                            <span className="text-xs text-muted-foreground mb-4">per maand</span>
                            <div className="w-full space-y-2 text-sm text-left flex-1">
                              <div className="flex items-center gap-2">
                                <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                <span><strong>{abo.data}</strong> data</span>
                              </div>
                              {abo.kenmerken?.map((kenmerk, ki) => (
                                <div key={ki} className="flex items-center gap-2">
                                  <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                  <span className="text-xs">{kenmerk}</span>
                                </div>
                              )) || (
                                <>
                                  <div className="flex items-center gap-2">
                                    <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                    <span><strong>{abo.minuten}</strong> bellen & sms</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                    <span>EU-roaming inbegrepen</span>
                                  </div>
                                </>
                              )}
                            </div>
                            <Button asChild size="sm" className="w-full mt-4" style={{ backgroundColor: provider.kleur, color: 'white' }}>
                              <TrackedLink to="/contact" trackLabel="Aanvragen" trackLocation="mobiel-odido-bundel">Aanvragen</TrackedLink>
                            </Button>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>

                  {hasUnlimited && (
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      {unlimitedBundels.map((abo, i) => (
                        <motion.div key={abo.naam} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                          <Card className={`h-full relative overflow-hidden transition-shadow hover:shadow-lg ${abo.populair ? 'ring-2 ring-offset-2' : ''}`} style={{ borderColor: provider.kleur, boxShadow: `0 0 0 2px ${provider.kleur}`, ...(abo.populair ? { ['--tw-ring-color' as string]: provider.kleur } : {}) }}>
                            {abo.populair && (
                              <div className="absolute top-0 left-0 right-0 text-center text-[10px] font-bold uppercase tracking-wider text-white py-0.5" style={{ backgroundColor: provider.kleur }}>
                                ⭐ Meest gekozen
                              </div>
                            )}
                            <div className={`${abo.populair ? 'mt-5' : ''} absolute top-0 left-0 right-0 text-center text-xs font-semibold text-white py-1 flex items-center justify-center gap-1.5`} style={{ backgroundColor: provider.kleur }}>
                              <Smartphone className="h-3 w-3" />
                              {abo.naam}
                            </div>
                            <CardContent className={`p-6 flex flex-col items-center text-center ${abo.populair ? 'pt-14' : 'pt-10'} h-full`}>
                              <div className="text-3xl font-bold mb-1" style={{ color: provider.kleur }}>{abo.prijs}</div>
                              <span className="text-xs text-muted-foreground mb-6">per maand</span>
                              <div className="w-full space-y-3 text-sm text-left flex-1">
                                <div className="flex items-center gap-2">
                                  <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                  <span><strong>{abo.data}</strong> data</span>
                                </div>
                                {abo.kenmerken?.map((kenmerk, ki) => (
                                  <div key={ki} className="flex items-center gap-2">
                                    <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                    <span>{kenmerk}</span>
                                  </div>
                                )) || (
                                  <>
                                    <div className="flex items-center gap-2">
                                      <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                      <span><strong>{abo.minuten}</strong> bellen & sms</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                      <span>EU-roaming inbegrepen</span>
                                    </div>
                                  </>
                                )}
                              </div>
                              <Button asChild className="w-full mt-6" style={{ backgroundColor: provider.kleur, color: 'white' }}>
                                <TrackedLink to="/contact" trackLabel="Aanvragen" trackLocation="mobiel-odido-unlimited">Aanvragen</TrackedLink>
                              </Button>
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Data Only Tablet/Laptop */}
          {provider.tabletAbonnementen && provider.tabletAbonnementen.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-muted">
                  <Tablet className="h-5 w-5 text-muted-foreground" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold">Data Only — Tablet & Laptop</h2>
              </div>
              <p className="text-muted-foreground mb-8 ml-[52px]">Mobiel internet voor je tablet of laptop · Alleen data, geen bellen of sms</p>

              {/* Rij 1: eerste 4 bundels (4 kolommen) */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
                {provider.tabletAbonnementen.slice(0, 4).map((abo, i) => (
                  <motion.div key={abo.naam} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                    <Card className="h-full relative overflow-hidden transition-shadow hover:shadow-lg border-dashed border-2" style={{ borderColor: `${provider.kleur}60` }}>
                      <div className="absolute top-0 left-0 right-0 text-center text-xs font-semibold py-1 flex items-center justify-center gap-1.5" style={{ backgroundColor: `${provider.kleur}15`, color: provider.kleur }}>
                        <Wifi className="h-3 w-3" />
                        {abo.naam}
                      </div>
                      <CardContent className="p-6 flex flex-col items-center text-center pt-10 h-full">
                        <div className="text-3xl font-bold mb-1" style={{ color: provider.kleur }}>{abo.prijs}</div>
                        <span className="text-xs text-muted-foreground mb-6">per maand</span>
                        <div className="w-full space-y-3 text-sm text-left flex-1">
                          <div className="flex items-center gap-2">
                            <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                            <span><strong>{abo.data}</strong> data</span>
                          </div>
                          {abo.kenmerken?.map((kenmerk, ki) => (
                            <div key={ki} className="flex items-center gap-2">
                              <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                              <span>{kenmerk}</span>
                            </div>
                          ))}
                        </div>
                        <Button asChild variant="outline" className="w-full mt-6" style={{ borderColor: provider.kleur, color: provider.kleur }}>
                          <TrackedLink to="/contact" trackLabel="Aanvragen" trackLocation="mobiel-odido-extra">Aanvragen</TrackedLink>
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              {/* Rij 2: Unlimited bundels (3 kolommen) */}
              {provider.tabletAbonnementen.length > 4 && (
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {provider.tabletAbonnementen.slice(4).map((abo, i) => (
                    <motion.div key={abo.naam} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                      <Card className="h-full relative overflow-hidden transition-shadow hover:shadow-lg border-dashed border-2" style={{ borderColor: `${provider.kleur}60` }}>
                        <div className="absolute top-0 left-0 right-0 text-center text-xs font-semibold py-1 flex items-center justify-center gap-1.5" style={{ backgroundColor: `${provider.kleur}15`, color: provider.kleur }}>
                          <Wifi className="h-3 w-3" />
                          {abo.naam}
                        </div>
                        <CardContent className="p-6 flex flex-col items-center text-center pt-10 h-full">
                          <div className="text-3xl font-bold mb-1" style={{ color: provider.kleur }}>{abo.prijs}</div>
                          <span className="text-xs text-muted-foreground mb-6">per maand</span>
                          <div className="w-full space-y-3 text-sm text-left flex-1">
                            <div className="flex items-center gap-2">
                              <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                              <span><strong>{abo.data}</strong> data</span>
                            </div>
                            {abo.kenmerken?.map((kenmerk, ki) => (
                              <div key={ki} className="flex items-center gap-2">
                                <Check className="h-4 w-4 shrink-0" style={{ color: provider.kleur }} />
                                <span>{kenmerk}</span>
                              </div>
                            ))}
                          </div>
                          <Button asChild variant="outline" className="w-full mt-6" style={{ borderColor: provider.kleur, color: provider.kleur }}>
                            <TrackedLink to="/contact" trackLabel="Aanvragen" trackLocation="mobiel-odido-extra">Aanvragen</TrackedLink>
                          </Button>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Buitenlandbundels */}
      {hasBuitenland && (
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container">
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <span className="h-1 w-10 rounded-full" style={{ background: provider.kleur }} />
                <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: provider.kleur }}>Internationaal</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">
                Buitenlandbundels <span className="text-gradient">Zakelijk Mobiel</span>
              </h2>
              <p className="mt-2 text-muted-foreground max-w-2xl">
                Voorkom onverwachte kosten op zakenreis. Activeer vooraf een buitenlandbundel en betaal alleen bij gebruik.
              </p>
            </div>

            {/* EU Roaming blok */}
            <div className="mb-8 rounded-2xl overflow-hidden shadow-lg">
              <div className="p-6 md:p-8" style={{ background: `linear-gradient(135deg, ${provider.kleur}, ${provider.kleur}cc)` }}>
                <div className="flex items-center gap-3 mb-3">
                  <Globe className="h-6 w-6 text-white" />
                  <h3 className="text-xl md:text-2xl font-bold text-white">Voordelig mobiel bellen en internetten in het buitenland</h3>
                </div>
                <p className="text-white/90 mb-6 max-w-3xl">
                  Alle Odido zakelijke abonnementen zijn binnen de EU net zo te gebruiken als in Nederland. Je belt, sms't en internett zonder extra kosten. Buiten je bundel? Je blijft gewoon online, maar dan minder snel.
                </p>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 md:p-6">
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Check className="h-5 w-5" />
                    Gebruik je abonnement in deze landen tegen dezelfde tarieven
                  </h4>
                  <p className="text-white/85 text-sm leading-relaxed">
                    België, Bulgarije, Canarische Eilanden, Cyprus (Griekse deel), Denemarken, Duitsland, Estland, Faeröer Eilanden, Finland, Frankrijk (incl. Martinique, Réunion, Mayotte, Frans-Guyana, Guadeloupe en Saint-Martin), Griekenland, Hongarije, Ierland, IJsland, Italië, Kroatië, Letland, Liechtenstein, Litouwen, Luxemburg, Malta, Moldavië, Noordzee (Tampnet), Noorwegen, Oekraïne, Oostenrijk, Polen, Portugal (incl. Madeira en de Azoren), Roemenië, San Marino, Slovenië, Slowakije, Spanje (incl. Gibraltar), Tsjechië, Vaticaanstad, Verenigd Koninkrijk (incl. Jersey, Guernsey en Isle of Man), Zweden, Zwitserland
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-8 lg:grid-cols-1">
              {provider.buitenlandbundels!.map((bundel, i) => (
                <BundelCard key={bundel.naam} bundel={bundel} index={i} providerKleur={provider.kleur} />
              ))}
              {provider.belBuitenEU && <BelBuitenEUCard data={provider.belBuitenEU} providerKleur={provider.kleur} />}
            </div>

            <p className="mt-6 text-xs text-muted-foreground max-w-2xl">
              Deze bundels staan bij ons standaard altijd aan. Prijzen zijn exclusief BTW.
            </p>
          </div>
        </section>
      )}

      {/* Extra bundels */}
      {!hasBuitenland && provider.extraBundels.length > 0 && (
        <section className="py-16 bg-muted/30">
          <div className="container">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Extra bundels & opties</h2>
            <p className="text-muted-foreground mb-10">Voeg extra's toe aan je abonnement, ook maandelijks opzegbaar.</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {provider.extraBundels.map((bundel, i) => (
                <motion.div key={bundel.naam} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
                  <Card className="h-full hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold mb-1">{bundel.naam}</h3>
                          <p className="text-sm text-muted-foreground">{bundel.beschrijving}</p>
                        </div>
                        <Badge variant="secondary" className="shrink-0 font-semibold">{bundel.prijs}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Veelgestelde vragen */}
      <section className="py-16 md:py-24">
        <div className="container">
          <motion.div className="mb-10" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="flex items-center gap-3 mb-3">
              <span className="h-1 w-10 rounded-full" style={{ background: provider.kleur }} />
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: provider.kleur }}>Support</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold">
              Veelgestelde <span className="text-gradient">vragen</span>
            </h2>
            <p className="mt-2 text-muted-foreground max-w-2xl">
              Alles over bellen, internetten en roaming in het buitenland met Odido Zakelijk.
            </p>
          </motion.div>

          {/* Automatisch internet buiten de EU */}
          <motion.div className="mb-10" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <HelpCircle className="h-5 w-5" style={{ color: provider.kleur }} />
              Meer over automatisch internet buiten de EU
            </h3>
            <Accordion type="single" collapsible className="space-y-3">
              <AccordionItem value="auto-1" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">
                  Met welke abonnementen krijg ik automatisch internet buiten de EU?
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  Met Unlimited Plus en Unlimited Premium krijg je automatisch internet in veel landen buiten de EU. Je hoeft niets aan te zetten — zodra je aankomt, werkt je data gewoon. Dit geldt ook voor @Work-abonnementen met deze bundels.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="auto-2" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">
                  In welke landen buiten de EU krijg ik automatisch internet?
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  Automatisch internet is beschikbaar in landen in Zone 1 en Zone 2, waaronder de VS, Canada, Turkije, Thailand, Australië en nog veel meer. Bekijk de volledige lijst bij de zone-informatie op deze pagina.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="auto-3" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">
                  Welke uitzonderingen zijn er?
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  Automatisch internet geldt alleen voor Unlimited Plus en Unlimited Premium (inclusief @Work-varianten). Met andere abonnementen kun je een losse buitenlandbundel activeren vóórdat je vertrekt.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="auto-4" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">
                  Ik heb een ander abonnement. Wat kost internetten buiten de EU?
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  Zonder buitenlandbundel betaal je het standaard dagtarief van de zone waarin je je bevindt. Om kosten te beheersen raden we aan vooraf een buitenlandbundel te activeren. Bekijk de tarieven per zone bovenaan deze pagina.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </motion.div>

          {/* Tabbed FAQ */}
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Tabs defaultValue="algemeen" className="w-full">
              <TabsList className="w-full flex mb-6 bg-muted/50 p-1 rounded-xl">
                <TabsTrigger value="algemeen" className="flex-1 text-xs sm:text-sm">Algemene vragen</TabsTrigger>
                <TabsTrigger value="zones" className="flex-1 text-xs sm:text-sm">Vragen over zones</TabsTrigger>
                <TabsTrigger value="netwerk" className="flex-1 text-xs sm:text-sm">4G en 5G in het buitenland</TabsTrigger>
              </TabsList>

              <TabsContent value="algemeen">
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="alg-1" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">Valt bellen naar de EU binnen mijn abonnement?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      Nee, bellen vanuit Nederland naar een EU-land valt niet standaard binnen je bundel. Hiervoor betaal je het vaste tarief per minuut. Bellen vanuit de EU naar andere EU-landen (inclusief Nederland) is wél inbegrepen — dat valt onder Roam like at Home.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="alg-2" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">Welke voorwaarden gelden er voor roaming?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      Roaming binnen de EU is inbegrepen (Roam like at Home). Je gebruikt je bundel zoals in Nederland. Bij langdurig of overmatig roaminggebruik kan Odido een toeslag in rekening brengen conform de Europese fair-use-regels. Buiten de EU gelden de zone-tarieven.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="alg-3" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">Waarom werkt mijn internet niet in het buitenland?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      Controleer of dataroaming is ingeschakeld op je toestel (bij iOS: Instellingen → Mobiel netwerk → Dataroaming. Bij Android: Instellingen → Verbindingen → Mobiele netwerken → Dataroaming). Lukt het nog niet? Herstart je telefoon of selecteer handmatig een lokaal netwerk.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="alg-4" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">Is mijn telefoon geschikt voor bellen in het buitenland?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      Vrijwel alle moderne smartphones ondersteunen internationale roaming. Je telefoon moet wel ontgrendeld zijn (geen simlock) en de juiste frequentiebanden ondersteunen. Bij twijfel: neem contact met ons op, dan checken we het voor je.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="alg-5" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">Kan ik bellen naar 112 in het buitenland?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      Ja, 112 is het internationale alarmnummer binnen de EU en in veel andere landen. Je kunt altijd 112 bellen, ook zonder beltegoed of met een geblokkeerde sim. In sommige landen buiten de EU kan het lokale alarmnummer anders zijn.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="alg-6" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">Hoe kan ik zien hoeveel MB's ik nog over heb?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      Je kunt je verbruik bekijken in de My Odido-app of via de Mijn Odido-portal. Daarnaast stuurt Odido je een sms wanneer je 80% en 100% van je databundel hebt verbruikt.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="alg-7" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">Wat zijn de voorwaarden van Roam like at Home?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      Roam like at Home betekent dat je in de EU je abonnement gebruikt alsof je in Nederland bent — bellen, sms'en en internetten tegen dezelfde tarieven. De fair-use-policy van de EU bepaalt dat dit bedoeld is voor incidenteel gebruik (vakantie, zakenreis), niet voor permanent verblijf in het buitenland.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="alg-8" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">Waar kan ik de landcodes vinden?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      Landcodes gebruik je om internationaal te bellen. Bijvoorbeeld: +31 voor Nederland, +32 voor België, +49 voor Duitsland. Een volledig overzicht vind je op de website van de ITU of vraag het aan ons — we helpen je graag.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </TabsContent>

              <TabsContent value="zones">
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="zone-1" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">Welke landen vallen in welke zones?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground space-y-3">
                      <p>Odido hanteert vier zones voor buitenlandtarieven:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li><strong>EU/EER:</strong> Alle EU-landen plus IJsland, Liechtenstein, Noorwegen, Verenigd Koninkrijk en Zwitserland — gebruik je bundel als in Nederland.</li>
                        <li><strong>Zone 1:</strong> O.a. de VS, Canada, Turkije, Australië, Japan, Nieuw-Zeeland, Thailand, Indonesië.</li>
                        <li><strong>Zone 2:</strong> O.a. Brazilië, China, India, Mexico, Zuid-Afrika, Rusland, Oekraïne.</li>
                        <li><strong>Zone 3:</strong> O.a. Afghanistan, Cuba, Somalië, Syrië en andere landen met hogere roamingkosten.</li>
                      </ul>
                      <p>Bekijk de exacte landenlijst per zone in de bundelkaarten op deze pagina.</p>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="zone-2" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">Wat betaal ik als ik op de Noordzee reis?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      Op de Noordzee kun je verbinding maken via het Tampnet-netwerk. Dit valt onder de EU-roamingtarieven, dus je gebruikt je bundel zoals in Nederland. Let op: zodra je buiten het Tampnet-bereik bent (open zee), kun je verbinding maken met een satellietnetwerk — daar gelden veel hogere tarieven. Zet dataroaming uit als je dit wilt voorkomen.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="zone-3" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">Kan ik mijn belminuten en databundel gebruiken in Zwitserland, de VS en Turkije?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      Zwitserland valt onder de EU-/EER-zone, dus daar gebruik je je bundel als in Nederland. De VS en Turkije vallen onder Zone 1 — hiervoor kun je een buitenlandbundel activeren of betaal je het dagtarief van Zone 1.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="zone-4" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">Welke landen vallen onder de Europese Unie?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      De EU-roamingzone omvat alle 27 EU-lidstaten plus IJsland, Liechtenstein, Noorwegen (EER) en het Verenigd Koninkrijk, Zwitserland, Moldavië, Oekraïne en San Marino. Een volledige lijst vind je in het EU-roamingblok bovenaan deze pagina.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="zone-5" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">In welke zone vallen Andorra en Monaco?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      Andorra en Monaco vallen niet onder de EU-roamingzone, ondanks hun ligging in Europa. Ze vallen onder Zone 1. Activeer een buitenlandbundel als je hier naartoe reist.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="zone-6" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">Welke landen vallen onder het Caribisch gebied?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      Het Caribisch deel van het Koninkrijk der Nederlanden (Bonaire, Sint-Eustatius, Saba, Curaçao, Aruba en Sint-Maarten) valt onder Zone 1. Let op: hoewel deze gebieden bij het Koninkrijk horen, gelden er andere tarieven dan in Europees Nederland.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </TabsContent>

              <TabsContent value="netwerk">
                <Accordion type="single" collapsible className="space-y-3">
                  <AccordionItem value="net-1" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">Kan ik gebruikmaken van 4G in het buitenland?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      Ja, in de meeste landen maak je automatisch verbinding met het snelste beschikbare netwerk, inclusief 4G. De beschikbaarheid hangt af van het lokale netwerk waarmee je telefoon verbinding maakt. In de EU is 4G vrijwel overal beschikbaar.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="net-2" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">Heb ik in het buitenland ook 5G?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      Dat hangt af van het land en het lokale netwerk. In steeds meer EU-landen is 5G beschikbaar voor roamers. Je telefoon moet wel 5G ondersteunen en je moet een 5G-abonnement hebben. Buiten de EU is 5G-roaming nog beperkt beschikbaar.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="net-3" className="rounded-xl border border-border bg-card px-6 shadow-sm">
                    <AccordionTrigger className="text-left font-semibold text-sm md:text-base hover:no-underline">Hoe snel is 5G in het buitenland?</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      De snelheid van 5G in het buitenland verschilt per land en netwerk. Over het algemeen kun je snelheden verwachten die vergelijkbaar zijn met 5G in Nederland, maar dit is afhankelijk van de lokale netwerkinfrastructuur. In de praktijk merk je vooral dat webpagina's en apps sneller laden en dat videobellen stabieler is.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16" style={{ background: `linear-gradient(135deg, ${provider.kleur}, ${provider.kleur}cc)` }}>
        <div className="container text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Direct aan de slag met {provider.naam}?</h2>
          <p className="text-white/80 mb-8 max-w-lg mx-auto">Neem contact op en we regelen alles voor je. Geen aansluitkosten, geen gedoe.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="hero-outline" className="border-white text-white hover:bg-white/10">
              <TrackedLink to="/contact" trackLabel="Neem contact op" trackLocation="mobiel-odido-cta">Neem contact op <ArrowRight className="ml-2 h-4 w-4" /></TrackedLink>
            </Button>
            <Button asChild size="lg" variant="ghost" className="text-white hover:bg-white/10">
              <Link to="/diensten/mobiel"><ArrowLeft className="mr-2 h-4 w-4" /> Alle netwerken bekijken</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default MobielOdidoPage;
